import { ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Tooltip } from '../components/Tooltip';
import { SidebarFlyout } from './SidebarFlyout';
import { isNavNodeVisible, type NavNode } from './sidebarConfig';

/** Indent per level. Level 3 lands at 40px, still comfortably inside a 240px rail. */
const INDENT = [10, 24, 38];

/**
 * Where a row's content starts — its icon, and the left end of any rule drawn above it.
 *
 * A nested row clears the parent's disclosure chevron by a further 22px. The divider reads
 * off the same number so it begins exactly where the items it separates begin, rather than
 * running the full width of the rail and cutting through the indent.
 */
function contentInset(depth: number): number {
  return INDENT[Math.min(depth, INDENT.length - 1)] + (depth > 0 ? 22 : 0);
}

/**
 * Hover-intent grace period. Long enough to cross the gap between the rail and the flyout
 * on a diagonal, short enough that the panel never feels stuck open.
 */
const CLOSE_DELAY_MS = 140;

/**
 * Recursive sidebar tree — reference §6.
 *
 * Permission gating is applied to the whole subtree: a group whose every child is hidden
 * disappears rather than expanding into nothing.
 *
 * When the rail is `collapsed` only the top level renders, as icons. Children stay
 * reachable — a group opens a flyout on hover or on keyboard activation — so collapsing
 * hides labels, never destinations.
 */
export function SidebarNav({
  nodes,
  depth = 0,
  collapsed = false,
}: {
  nodes: NavNode[];
  depth?: number;
  collapsed?: boolean;
}) {
  const { hasPermission } = useAuth();
  const visible = nodes.filter((node) => isNavNodeVisible(node, hasPermission));

  if (collapsed) {
    return (
      <ul className="flex flex-col items-center gap-0.5">
        {visible.map((node, index) => (
          <li key={node.key}>
            {node.divider && index > 0 ? <NavDivider depth={0} /> : null}
            <CollapsedNavItem node={node} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="flex flex-col gap-0.5">
      {visible.map((node, index) => (
        <li key={node.key}>
          {node.divider && index > 0 ? <NavDivider depth={depth} /> : null}
          {node.children ? (
            <NavGroup node={node} depth={depth} />
          ) : (
            <NavLeaf node={node} depth={depth} />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The rule above a node that starts a different KIND of destination.
 *
 * **Dashed, and inset to where the rows begin.** A solid full-width line reads as a border
 * between two regions of the rail; these are entries in one group, and the rule only says
 * they are of different kinds. Starting it at the items' own left edge keeps the indent
 * legible — a line through the indent makes the group look like two groups.
 *
 * `slate-300` rather than the lighter `slate-200`: the rail's ground is `--color-rail`
 * (#eef2f7), and slate-200 on it lands about 1.09:1 away, which reads as no line at all —
 * the same trap the rail's hover has.
 *
 * Never above the first visible node: a gate that hid everything before it would leave a
 * rule separating the group from nothing.
 */
function NavDivider({ depth }: { depth: number }) {
  return (
    <div
      aria-hidden="true"
      className="my-1 border-t border-dashed border-slate-300"
      style={{ marginLeft: contentInset(depth) }}
    />
  );
}

/** The icon-only rail entry, and the flyout it opens. */
function CollapsedNavItem({ node }: { node: NavNode }) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openNow = useCallback(
    (fromKeyboard = false) => {
      cancelClose();
      setOpenedByKeyboard(fromKeyboard);
      setOpen(true);
    },
    [cancelClose],
  );

  const close = useCallback(
    (options?: { restoreFocus?: boolean }) => {
      cancelClose();
      setOpen(false);

      if (options?.restoreFocus) {
        triggerRef.current?.focus();
      }
    },
    [cancelClose],
  );

  // Leaving the trigger on the way to the panel must not dismiss it mid-travel.
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);

  /*
    Only the pending-close timer needs cleanup. There is deliberately no route-change
    effect: navigating from inside the flyout closes it through onRequestClose, and a
    navigation from anywhere else is a pointerdown outside the panel, which closes it too.
  */
  useEffect(() => cancelClose, [cancelClose]);

  const Icon = node.icon;
  const fallback = node.label.slice(0, 2).toUpperCase();

  const chrome =
    'relative flex h-10 w-10 items-center justify-center rounded-r-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300';

  const glyph = Icon ? (
    <Icon className="h-4 w-4" aria-hidden="true" />
  ) : (
    <span className="text-[11px] font-medium" aria-hidden="true">
      {fallback}
    </span>
  );

  // A leaf still navigates straight from the rail; only its label moves into a tooltip.
  if (!node.children) {
    if (node.sample || !node.to) {
      return (
        <Tooltip label={`${node.label} — sample entry, not wired to a route.`}>
          <span
            id={`nav-${node.key}`}
            aria-label={node.label}
            className={`${chrome} cursor-default text-slate-400`}
          >
            {glyph}
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip label={node.label}>
        <NavLink
          to={node.to}
          id={`nav-${node.key}`}
          aria-label={node.label}
          className={({ isActive }) =>
            `${chrome} ${
              isActive
                ? 'text-slate-900'
                : 'text-slate-600 hover:bg-slate-300 hover:text-slate-800'
            }`
          }
        >
          {({ isActive }) => (
            <>
              {isActive ? <span aria-hidden="true" className="absolute left-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-r bg-brand-500" /> : null}
              {glyph}
            </>
          )}
        </NavLink>
      </Tooltip>
    );
  }

  return (
    <>
      <button
        type="button"
        ref={(element) => {
          triggerRef.current = element;
        }}
        id={`nav-${node.key}`}
        aria-label={node.label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `nav-flyout-${node.key}` : undefined}
        onPointerEnter={() => openNow()}
        onPointerLeave={scheduleClose}
        /*
          Deliberately NOT open-on-focus. Escape restores focus to this button, and an
          onFocus that opens would reopen the panel the instant it was dismissed. Keyboard
          users open it with Enter or Space, which is the menu-button pattern anyway.
          detail === 0 is how a click reports that it came from the keyboard.
        */
        onClick={(event) => (open ? close() : openNow(event.detail === 0))}
        className={`${chrome} ${
          open
            ? 'bg-slate-300 text-slate-800'
            : 'text-slate-600 hover:bg-slate-300 hover:text-slate-800'
        }`}
      >
        {glyph}
      </button>

      {open ? (
        <SidebarFlyout
          node={node}
          triggerRef={triggerRef}
          id={`nav-flyout-${node.key}`}
          onRequestClose={close}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          autoFocus={openedByKeyboard}
        />
      ) : null}
    </>
  );
}

function NavGroup({ node, depth }: { node: NavNode; depth: number }) {
  const location = useLocation();

  // A group containing the current route starts open, so a deep link never lands the user
  // on a page whose sidebar entry is collapsed out of sight.
  const containsRoute = useCallback(
    function walk(n: NavNode): boolean {
      if (n.to && location.pathname.startsWith(n.to)) return true;
      return (n.children ?? []).some(walk);
    },
    [location.pathname],
  );

  const [open, setOpen] = useState<boolean>(() => {
    if (containsRoute(node)) return true;

    try {
      const stored = localStorage.getItem(`nav.${node.key}`);

      // Expanded by default: a first-time visitor should see the whole tree rather than
      // having to discover it. Only an explicit collapse is remembered.
      return stored === null ? true : stored === 'open';
    } catch {
      return true;
    }
  });

  function toggle() {
    setOpen((current) => {
      const next = !current;

      try {
        localStorage.setItem(`nav.${node.key}`, next ? 'open' : 'closed');
      } catch {
        // Remembering the state is a convenience, never a requirement.
      }

      return next;
    });
  }

  return (
    <>
      <button
        type="button"
        id={`nav-${node.key}`}
        onClick={toggle}
        aria-expanded={open}
        style={{ paddingLeft: INDENT[Math.min(depth, INDENT.length - 1)] }}
        className="flex w-full items-center gap-2.5 rounded-r-lg py-2 pr-2 text-sm text-slate-600 transition hover:bg-slate-300 hover:text-slate-800"
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        {node.icon ? <node.icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
        <span className="min-w-0 truncate">{node.label}</span>
      </button>

      {open ? (
        <div className="mt-0.5">
          <SidebarNav nodes={node.children ?? []} depth={depth + 1} />
        </div>
      ) : null}
    </>
  );
}

function NavLeaf({ node, depth }: { node: NavNode; depth: number }) {
  const padding = INDENT[Math.min(depth, INDENT.length - 1)];

  // A sample entry renders in the tree but goes nowhere, and says so.
  if (node.sample || !node.to) {
    return (
      <Tooltip label="Sample entry — not wired to a route.">
        <span
          id={`nav-${node.key}`}
          style={{ paddingLeft: padding + 22 }}
          className="flex w-full cursor-default items-center gap-2.5 rounded-r-lg py-2 pr-2 text-sm text-slate-400"
        >
          {node.icon ? <node.icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
          <span className="min-w-0 truncate">{node.label}</span>
        </span>
      </Tooltip>
    );
  }

  return (
    <NavLink
      to={node.to}
      id={`nav-${node.key}`}
      style={{ paddingLeft: contentInset(depth) }}
      className={({ isActive }) =>
        [
          'relative flex items-center gap-2.5 rounded-r-lg py-2 pr-2 text-sm transition',
          isActive
            ? // A brand-yellow leading edge carries the selection; the label stays black.
              'text-slate-900'
            : /*
                A SECONDARY entry rests one step lighter — configuration, not work. Only the
                REST tone moves: hover and active are untouched, so the gap that carries the
                selection widens rather than narrows (slate-900 against slate-500), which is
                the signal the 1.92:1 brand bar is defensible because of.
              */
              node.secondary
              ? 'text-slate-500 hover:bg-slate-300 hover:text-slate-800'
              : 'text-slate-600 hover:bg-slate-300 hover:text-slate-800',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? <span aria-hidden="true" className="absolute left-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-r bg-brand-500" /> : null}
          {node.icon ? <node.icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
          <span className="min-w-0 truncate">{node.label}</span>
        </>
      )}
    </NavLink>
  );
}
